// Shared allocation-free TRS/quaternion math for animation and articulated presentation.
export type Quaternion = readonly [number, number, number, number];
export function multiplyQuaternion(a: Quaternion, b: Quaternion): Quaternion {
  const [x, y, z, w] = a, [u, v, s, t] = b;
  return [w * u + x * t + y * s - z * v, w * v - x * s + y * t + z * u,
    w * s + x * v - y * u + z * t, w * t - x * u - y * v - z * s];
}
export function inverseQuaternion(q: Quaternion): Quaternion { return [-q[0], -q[1], -q[2], q[3]]; }
export function eulerQuaternion(x: number, y = 0, z = 0): Quaternion {
  const sx = Math.sin(x / 2), cx = Math.cos(x / 2), sy = Math.sin(y / 2), cy = Math.cos(y / 2), sz = Math.sin(z / 2), cz = Math.cos(z / 2);
  return [sx * cy * cz + cx * sy * sz, cx * sy * cz - sx * cy * sz, cx * cy * sz + sx * sy * cz, cx * cy * cz - sx * sy * sz];
}
export function betweenDirections(a: readonly number[], b: readonly number[]): Quaternion {
  const al = Math.hypot(a[0]!, a[1]!, a[2]!) || 1, bl = Math.hypot(b[0]!, b[1]!, b[2]!) || 1;
  const ax = a[0]! / al, ay = a[1]! / al, az = a[2]! / al, bx = b[0]! / bl, by = b[1]! / bl, bz = b[2]! / bl;
  const w = 1 + ax * bx + ay * by + az * bz;
  if (w < 1e-6) return Math.abs(ax) < 0.8 ? [0, az / (Math.hypot(ay, az) || 1), -ay / (Math.hypot(ay, az) || 1), 0]
    : [-az / (Math.hypot(ax, az) || 1), 0, ax / (Math.hypot(ax, az) || 1), 0];
  const x = ay * bz - az * by, y = az * bx - ax * bz, z = ax * by - ay * bx, length = Math.hypot(x, y, z, w);
  return [x / length, y / length, z / length, w / length];
}

export function slerpInto(
  target: Float32Array,
  targetOffset: number,
  a: Float32Array,
  aOffset: number,
  b: Float32Array,
  bOffset: number,
  amount: number,
): void {
  const ax = a[aOffset]!;
  const ay = a[aOffset + 1]!;
  const az = a[aOffset + 2]!;
  const aw = a[aOffset + 3]!;
  let bx = b[bOffset]!;
  let by = b[bOffset + 1]!;
  let bz = b[bOffset + 2]!;
  let bw = b[bOffset + 3]!;
  let cosine = ax * bx + ay * by + az * bz + aw * bw;
  if (cosine < 0) {
    cosine = -cosine;
    bx = -bx;
    by = -by;
    bz = -bz;
    bw = -bw;
  }
  let scaleA = 1 - amount;
  let scaleB = amount;
  if (cosine < 0.9995) {
    const angle = Math.acos(Math.max(-1, Math.min(1, cosine)));
    const inverseSine = 1 / Math.sin(angle);
    scaleA = Math.sin((1 - amount) * angle) * inverseSine;
    scaleB = Math.sin(amount * angle) * inverseSine;
  }
  const x = ax * scaleA + bx * scaleB;
  const y = ay * scaleA + by * scaleB;
  const z = az * scaleA + bz * scaleB;
  const w = aw * scaleA + bw * scaleB;
  const inverseLength = 1 / (Math.hypot(x, y, z, w) || 1);
  target[targetOffset] = x * inverseLength;
  target[targetOffset + 1] = y * inverseLength;
  target[targetOffset + 2] = z * inverseLength;
  target[targetOffset + 3] = w * inverseLength;
}

export function writeTrs(
  target: Float32Array,
  offset: number,
  translations: Float32Array,
  translationOffset: number,
  rotations: Float32Array,
  rotationOffset: number,
  scales: Float32Array,
  scaleOffset: number,
): void {
  const x = rotations[rotationOffset]!;
  const y = rotations[rotationOffset + 1]!;
  const z = rotations[rotationOffset + 2]!;
  const w = rotations[rotationOffset + 3]!;
  const x2 = x + x;
  const y2 = y + y;
  const z2 = z + z;
  const xx = x * x2;
  const xy = x * y2;
  const xz = x * z2;
  const yy = y * y2;
  const yz = y * z2;
  const zz = z * z2;
  const wx = w * x2;
  const wy = w * y2;
  const wz = w * z2;
  const sx = scales[scaleOffset]!;
  const sy = scales[scaleOffset + 1]!;
  const sz = scales[scaleOffset + 2]!;
  target[offset] = (1 - (yy + zz)) * sx;
  target[offset + 1] = (xy + wz) * sx;
  target[offset + 2] = (xz - wy) * sx;
  target[offset + 3] = 0;
  target[offset + 4] = (xy - wz) * sy;
  target[offset + 5] = (1 - (xx + zz)) * sy;
  target[offset + 6] = (yz + wx) * sy;
  target[offset + 7] = 0;
  target[offset + 8] = (xz + wy) * sz;
  target[offset + 9] = (yz - wx) * sz;
  target[offset + 10] = (1 - (xx + yy)) * sz;
  target[offset + 11] = 0;
  target[offset + 12] = translations[translationOffset]!;
  target[offset + 13] = translations[translationOffset + 1]!;
  target[offset + 14] = translations[translationOffset + 2]!;
  target[offset + 15] = 1;
}

export function multiplyMatrices(
  target: Float32Array,
  targetOffset: number,
  a: Float32Array,
  aOffset: number,
  b: Float32Array,
  bOffset: number,
): void {
  for (let column = 0; column < 4; column += 1) {
    const bx = b[bOffset + column * 4]!;
    const by = b[bOffset + column * 4 + 1]!;
    const bz = b[bOffset + column * 4 + 2]!;
    const bw = b[bOffset + column * 4 + 3]!;
    target[targetOffset + column * 4] =
      a[aOffset]! * bx + a[aOffset + 4]! * by + a[aOffset + 8]! * bz + a[aOffset + 12]! * bw;
    target[targetOffset + column * 4 + 1] =
      a[aOffset + 1]! * bx + a[aOffset + 5]! * by + a[aOffset + 9]! * bz + a[aOffset + 13]! * bw;
    target[targetOffset + column * 4 + 2] =
      a[aOffset + 2]! * bx + a[aOffset + 6]! * by + a[aOffset + 10]! * bz + a[aOffset + 14]! * bw;
    target[targetOffset + column * 4 + 3] =
      a[aOffset + 3]! * bx + a[aOffset + 7]! * by + a[aOffset + 11]! * bz + a[aOffset + 15]! * bw;
  }
}

export function copyMatrix(target: Float32Array, targetOffset: number, source: Float32Array, sourceOffset: number): void {
  for (let index = 0; index < 16; index += 1) target[targetOffset + index] = source[sourceOffset + index]!;
}
